import { Link } from "react-router-dom";
import { Linkedin, Instagram, Facebook, Mail, ArrowUpRight, MapPin } from "lucide-react";
import { NAV_LINKS, CONTACT } from "@/data/site";
import Logo from "@/components/Logo";
import NewsletterSignup from "@/components/NewsletterSignup";

const QUICK_LINKS = [
  { to: "/services", label: "Web Applications", testid: "footer-svc-web" },
  { to: "/services", label: "Mobile Experiences", testid: "footer-svc-mobile" },
  { to: "/services", label: "AI & Automation", testid: "footer-svc-ai" },
  { to: "/services", label: "Digital Marketing", testid: "footer-svc-marketing" },
  { to: "/services", label: "IT Solutions", testid: "footer-svc-it" },
];

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="border-t border-zinc-200 bg-[#FAFAF7] mt-24">
      {/* CTA banner */}
      <div className="border-b border-zinc-200">
        <div className="container-tight py-14 grid md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <p className="label-mono">Have a project in mind?</p>
            <h3 className="mt-4 font-display text-3xl md:text-5xl text-zinc-900 tracking-tighter leading-[1.05]">
              Let&apos;s build something that
              <br /><span className="text-zinc-400">moves the needle.</span>
            </h3>
          </div>
          <div className="md:col-span-4 md:flex md:justify-end">
            <Link
              to="/contact"
              data-testid="footer-cta"
              className="inline-flex items-center gap-2 bg-zinc-900 text-white px-6 py-3.5 text-sm font-medium hover:bg-zinc-700 transition-colors"
            >
              Start the conversation
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      <div className="container-tight py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Brand + Newsletter */}
          <div className="md:col-span-5">
            <Logo showWordmark={false} size="h-10" />
            <p className="mt-5 max-w-md text-zinc-600 text-[15px] leading-relaxed">
              Engineering studios for ambitious teams. We design, build, and scale
              web, mobile, AI, and digital products from Coimbatore and Muscat —
              for clients across four continents.
            </p>
            <div className="mt-6">
              <NewsletterSignup />
            </div>
          </div>

          {/* Sitemap */}
          <div className="md:col-span-2">
            <p className="label-mono mb-4">Sitemap</p>
            <ul className="space-y-2">
              {NAV_LINKS.map((l) => (
                <li key={l.to + l.label}>
                  <Link
                    to={l.to}
                    data-testid={`footer-${l.testid}`}
                    className="text-zinc-700 hover:text-zinc-900 text-[15px] inline-flex items-center gap-1.5 group"
                  >
                    {l.label}
                    <ArrowUpRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Capabilities */}
          <div className="md:col-span-2">
            <p className="label-mono mb-4">Capabilities</p>
            <ul className="space-y-2">
              {QUICK_LINKS.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    data-testid={l.testid}
                    className="text-zinc-700 hover:text-zinc-900 text-[15px]"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Offices */}
          <div className="md:col-span-3">
            <p className="label-mono mb-4">Offices</p>
            <div className="space-y-5">
              {CONTACT.offices.map((o) => (
                <div key={o.city}>
                  <p className="text-zinc-900 text-sm font-medium flex items-center gap-2">
                    <MapPin className="w-3.5 h-3.5 text-zinc-500" /> {o.city}
                  </p>
                  <p className="text-zinc-600 text-[13px] whitespace-pre-line leading-relaxed mt-1.5">
                    {o.address}
                  </p>
                  <p className="text-zinc-500 text-[12px] font-mono-tech mt-1.5">{o.phone}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 flex items-center gap-2">
              <a
                href={CONTACT.linkedin}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-linkedin"
                aria-label="LinkedIn"
                className="inline-flex items-center justify-center w-9 h-9 border border-zinc-300 text-zinc-700 hover:text-zinc-900 hover:border-zinc-600 transition-colors"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a
                href={CONTACT.instagram}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-instagram"
                aria-label="Instagram"
                className="inline-flex items-center justify-center w-9 h-9 border border-zinc-300 text-zinc-700 hover:text-zinc-900 hover:border-zinc-600 transition-colors"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href={CONTACT.facebook}
                target="_blank"
                rel="noreferrer"
                data-testid="footer-facebook"
                aria-label="Facebook"
                className="inline-flex items-center justify-center w-9 h-9 border border-zinc-300 text-zinc-700 hover:text-zinc-900 hover:border-zinc-600 transition-colors"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href={`mailto:${CONTACT.email}`}
                data-testid="footer-email"
                className="inline-flex items-center gap-2 px-3 h-9 border border-zinc-300 text-zinc-700 hover:text-zinc-900 hover:border-zinc-600 text-xs"
              >
                <Mail className="w-3.5 h-3.5" /> {CONTACT.email}
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-zinc-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-zinc-500 text-xs font-mono-tech">
            © {new Date().getFullYear()} AMAGICTECH SOLUTIONS · ALL RIGHTS RESERVED
          </p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Link to="/services" className="text-zinc-500 text-xs font-mono-tech hover:text-zinc-900">SERVICES</Link>
            <Link to="/portfolio" className="text-zinc-500 text-xs font-mono-tech hover:text-zinc-900">PORTFOLIO</Link>
            <Link to="/blog" className="text-zinc-500 text-xs font-mono-tech hover:text-zinc-900">BLOG</Link>
            <Link to="/contact" className="text-zinc-500 text-xs font-mono-tech hover:text-zinc-900">CONTACT</Link>
            <span className="text-zinc-500 text-xs font-mono-tech">CRAFTED IN COIMBATORE × MUSCAT</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
